/**
 * Human names for the routes the activity logger records.
 *
 * The logger stores raw pathnames ("/session-httraining/9f2.../1"), which are
 * unreadable in a report and leak ids that mean nothing to whoever reads it.
 * Every route in App.tsx is matched here on its leading segments; the ids in
 * between are ignored, and the trailing segment only matters where it changes
 * the page ("leaderboard" vs. the session itself).
 */

// Module slugs used by /low-tension/:module and /high-tension/:module.
const MODULES: Record<string, [string, string]> = {
    edit: ["Editare", "Edit"],
    functional: ["Funcționare", "Functional"],
    debugging: ["Depanare", "Debugging"],
    learning: ["Învățare", "Learning"],
    training: ["Antrenare", "Training"],
    testing: ["Testare", "Testing"],
};

/** [ro, en] for a route, keyed by its first segment. */
const PAGES: Record<string, [string, string]> = {
    "": ["Acasă", "Home"],
    "login": ["Autentificare", "Login"],
    "about-us": ["Despre noi", "About us"],
    "contact": ["Contact", "Contact"],
    "categories": ["Categorii", "Categories"],
    "students": ["Elevi", "Students"],
    "users": ["Utilizatori", "Users"],
    "activity-log": ["Jurnal de activitate", "Activity log"],
    "create-student": ["Elev nou", "New student"],
    "edit-student": ["Editare elev", "Edit student"],
    "edit-user": ["Editare utilizator", "Edit user"],
    "create-account": ["Cont nou", "New account"],
    "create-project": ["Proiect nou", "New project"],
    "edit-project": ["Editare proiect", "Edit project"],
    "project": ["Proiect", "Project"],
    "predefined-scheme": ["Schemă predefinită", "Predefined scheme"],
    "predefined-scheme-debug": ["Schemă predefinită — depanare", "Predefined scheme — debugging"],
    "training": ["Antrenament", "Training"],
    "high-tension-training": ["Antrenare — schemă", "Training — scheme"],
    "create-training-session": ["Sesiune nouă de antrenare", "New training session"],
    "options-testing": ["Opțiuni testare", "Testing options"],
    "options-learning": ["Opțiuni învățare", "Learning options"],
};

/**
 * Session routes: /<segment>/<sessionId>/<leaderboard | join>. The module name
 * is the same for both, only the suffix differs.
 */
const SESSIONS: Record<string, [string, string]> = {
    "session": ["Depanare", "Debugging"],
    "session-testing": ["Testare", "Testing"],
    "session-learning": ["Învățare", "Learning"],
    "session-httraining": ["Antrenare", "Training"],
    "session-htlearning": ["Învățare", "Learning"],
};

/** Turns a recorded pathname into something worth printing in a report. */
export const pageLabel = (path: string, en: boolean): string => {
    if (!path) return en ? "Unknown" : "Necunoscut";

    const segs = path.split("?")[0].split("/").filter(Boolean);
    const pick = (pair: [string, string]) => (en ? pair[1] : pair[0]);

    if (!segs.length) return pick(PAGES[""]);

    const [head, ...rest] = segs;

    if (SESSIONS[head]) {
        const isBoard = segs[segs.length - 1] === "leaderboard";
        const suffix = isBoard ? (en ? "leaderboard" : "clasament") : (en ? "session" : "sesiune");
        return `${pick(SESSIONS[head])} — ${suffix}`;
    }

    // /low-tension/:module and /high-tension/:module carry the module in the URL.
    if (head === "low-tension" || head === "high-tension") {
        const tension = head === "low-tension"
            ? (en ? "Low voltage" : "Joasă tensiune")
            : (en ? "High voltage" : "Înaltă tensiune");
        const mod = MODULES[rest[0]];
        return mod ? `${tension} — ${pick(mod)}` : tension;
    }

    if (PAGES[head]) return pick(PAGES[head]);

    // Unmapped route: at least drop the ids and title-case the slug, so a new
    // page shows up as "Ceva Nou" rather than as a raw path.
    return head.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

export default pageLabel;
